import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Server, Plus, Trash2, RefreshCw, Play, CheckCircle, 
  XCircle, Clock, FileText, Settings, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';
import { getAuthHeaders, getUser } from '../utils/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SFTPInbound = () => {
  const navigate = useNavigate();
  const user = getUser();
  const [configs, setConfigs] = useState([]);
  const [pullHistory, setPullHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddConfig, setShowAddConfig] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [pullingId, setPullingId] = useState(null);
  const [formData, setFormData] = useState({
    name: 'Choice Logistics',
    host: '',
    port: 22,
    username: '',
    password: '',
    remote_path: '/outbound',
    processed_path: '/outbound/processed',
    file_pattern: '*.csv',
    customer_code: 'CHOICE',
    is_active: true,
    auto_poll: false,
    poll_interval_minutes: 5
  });

  useEffect(() => {
    if (!user || !['admin', 'dispatcher'].includes(user.role)) {
      navigate('/dispatcher/login');
      return;
    }
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [configsRes, historyRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/sftp-inbound/configs`, { headers: getAuthHeaders() }),
        axios.get(`${BACKEND_URL}/api/sftp-inbound/pull-history`, { headers: getAuthHeaders() })
      ]);
      setConfigs(configsRes.data.configs || []);
      setPullHistory(historyRes.data.history || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddConfig = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${BACKEND_URL}/api/sftp-inbound/configs`,
        formData,
        { headers: getAuthHeaders() }
      );
      setShowAddConfig(false);
      setFormData({
        name: '',
        host: '',
        port: 22,
        username: '',
        password: '',
        remote_path: '/outbound',
        processed_path: '/outbound/processed',
        file_pattern: '*.csv',
        customer_code: 'CHOICE',
        is_active: true,
        auto_poll: false,
        poll_interval_minutes: 5
      });
      loadData();
    } catch (error) {
      console.error('Error adding config:', error);
      alert(error.response?.data?.detail || 'Failed to add configuration');
    }
  };

  const handleDelete = async (configId) => {
    if (!window.confirm('Are you sure you want to delete this configuration?')) return;
    
    try {
      await axios.delete(
        `${BACKEND_URL}/api/sftp-inbound/configs/${configId}`,
        { headers: getAuthHeaders() }
      );
      loadData();
    } catch (error) {
      console.error('Error deleting config:', error);
    }
  };

  const handleTest = async (configId) => {
    setTestingId(configId);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/sftp-inbound/configs/${configId}/test`,
        {},
        { headers: getAuthHeaders() }
      );
      alert(response.data.message);
    } catch (error) {
      alert(error.response?.data?.message || 'Connection test failed');
    } finally {
      setTestingId(null);
    }
  };

  const handlePull = async (configId) => {
    setPullingId(configId);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/sftp-inbound/configs/${configId}/pull`,
        {},
        { headers: getAuthHeaders() }
      );
      alert(response.data.message);
      // Refresh history after a short delay
      setTimeout(loadData, 2000);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to start pull');
    } finally {
      setPullingId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button onClick={() => navigate('/dispatcher/dashboard')} className="p-2 hover:bg-slate-100 rounded-lg">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-slate-900">SFTP Inbound Integration</h1>
              <p className="text-sm text-slate-500">Pull orders from customer SFTP servers</p>
            </div>
          </div>
          <Button onClick={() => setShowAddConfig(true)} className="bg-slate-800">
            <Plus className="h-4 w-4 mr-2" />
            Add Configuration
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Info Banner */}
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-800">Choice Logistics Integration</h3>
              <p className="text-sm text-blue-700 mt-1">
                Configure your SFTP connection to automatically pull orders from Choice Logistics. 
                Files will be parsed and converted to jobs in your dispatch system.
              </p>
            </div>
          </div>
        </Card>

        {/* Configurations */}
        <div>
          <h2 className="text-lg font-semibold text-slate-900 mb-4">SFTP Configurations</h2>
          
          {configs.length === 0 ? (
            <Card className="p-8 text-center bg-white">
              <Server className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">No Configurations</h3>
              <p className="text-slate-500 mb-4">Add an SFTP configuration to start pulling orders</p>
              <Button onClick={() => setShowAddConfig(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Configuration
              </Button>
            </Card>
          ) : (
            <div className="grid gap-4">
              {configs.map((config) => (
                <Card key={config.id} className="p-5 bg-white">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      <div className={`p-3 rounded-xl ${config.is_active ? 'bg-green-100' : 'bg-slate-100'}`}>
                        <Server className={`h-6 w-6 ${config.is_active ? 'text-green-600' : 'text-slate-400'}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{config.name}</h3>
                        <p className="text-sm text-slate-500 font-mono">{config.host}:{config.port}</p>
                        <div className="flex items-center space-x-4 mt-2 text-xs text-slate-500">
                          <span>Path: {config.remote_path}</span>
                          <span>Pattern: {config.file_pattern}</span>
                          <span>Customer: {config.customer_code}</span>
                        </div>
                        {config.last_poll && (
                          <div className="mt-2 text-xs">
                            <span className="text-slate-500">Last pull: {formatDate(config.last_poll)}</span>
                            {config.last_poll_result && (
                              <span className="ml-3 text-green-600">
                                {config.last_poll_result.jobs_created} jobs created
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleTest(config.id)}
                        disabled={testingId === config.id}
                      >
                        {testingId === config.id ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <Settings className="h-4 w-4" />
                        )}
                        <span className="ml-2">Test</span>
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handlePull(config.id)}
                        disabled={pullingId === config.id}
                        className="bg-blue-600 hover:bg-blue-500"
                      >
                        {pullingId === config.id ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <Play className="h-4 w-4" />
                        )}
                        <span className="ml-2">Pull Now</span>
                      </Button>
                      {user?.role === 'admin' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(config.id)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Pull History */}
        <div>
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Pull History</h2>
          <Card className="bg-white overflow-hidden">
            {pullHistory.length === 0 ? (
              <div className="p-8 text-center">
                <Clock className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                <p className="text-slate-500">No pull history yet</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Time</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Customer</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Files Found</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Processed</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Jobs Created</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {pullHistory.map((log, idx) => (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="py-3 px-4 text-sm">{formatDate(log.timestamp)}</td>
                      <td className="py-3 px-4 text-sm font-semibold">{log.customer_code}</td>
                      <td className="py-3 px-4 text-sm">{log.result?.files_found || 0}</td>
                      <td className="py-3 px-4 text-sm">{log.result?.files_processed || 0}</td>
                      <td className="py-3 px-4 text-sm font-semibold text-green-600">{log.result?.jobs_created || 0}</td>
                      <td className="py-3 px-4">
                        {log.result?.errors?.length > 0 ? (
                          <span className="flex items-center text-yellow-600 text-sm">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {log.result.errors.length} errors
                          </span>
                        ) : (
                          <span className="flex items-center text-green-600 text-sm">
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Success
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>
      </div>

      {/* Add Configuration Modal */}
      {showAddConfig && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleAddConfig} className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-slate-900">Add SFTP Configuration</h2>
                <button type="button" onClick={() => setShowAddConfig(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <XCircle className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Configuration Name *</label>
                    <Input
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="Choice Logistics"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Customer Code *</label>
                    <Input
                      required
                      value={formData.customer_code}
                      onChange={(e) => setFormData({...formData, customer_code: e.target.value.toUpperCase()})}
                      placeholder="CHOICE"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">SFTP Host *</label>
                    <Input
                      required
                      value={formData.host}
                      onChange={(e) => setFormData({...formData, host: e.target.value})}
                      placeholder="sftp.choicelogistics.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Port</label>
                    <Input
                      type="number"
                      value={formData.port}
                      onChange={(e) => setFormData({...formData, port: parseInt(e.target.value)})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Username *</label>
                    <Input
                      required
                      value={formData.username}
                      onChange={(e) => setFormData({...formData, username: e.target.value})}
                      placeholder="sabey_logistics"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      placeholder="••••••••"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Remote Path (Inbound Orders)</label>
                    <Input
                      value={formData.remote_path}
                      onChange={(e) => setFormData({...formData, remote_path: e.target.value})}
                      placeholder="/outbound"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">File Pattern</label>
                    <Input
                      value={formData.file_pattern}
                      onChange={(e) => setFormData({...formData, file_pattern: e.target.value})}
                      placeholder="*.csv"
                    />
                  </div>
                </div>

                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="font-semibold text-slate-900 mb-2">How it works</h3>
                  <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
                    <li>Choice Logistics drops order files in their SFTP server</li>
                    <li>Click "Pull Now" to fetch new order files</li>
                    <li>Files are parsed and jobs are created automatically</li>
                    <li>Processed files are moved to prevent duplicates</li>
                    <li>You can then assign jobs to your drivers</li>
                  </ol>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-slate-200">
                <Button type="button" variant="outline" onClick={() => setShowAddConfig(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-slate-800">
                  Add Configuration
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SFTPInbound;
